import { useState } from 'owa-react';
import { AccTabList } from 'accelerator-common/lib/AccTabList';
import { PlacesFluentProvider } from 'hybridspace-common/lib/PlacesFluentProvider';
import { addDocumentEventListener } from 'owa-event-listener';
import LineHorizontal3 from 'owa-fluent-icons-svg/lib/icons/LineHorizontal3Regular';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import React from 'react';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid using FocusTrapZone from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use the equivalent [Tabster](https://tabster.io/) hook instead.
 *	> Avoid using Icon from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use a JSX SVG icon from '@fluentui/react-icons' instead. */
import { FocusTrapZone, Icon } from '@fluentui/react';
import {
    Button,
    Caption1Strong,
    Divider,
    DrawerBody,
    mergeClasses,
    OverlayDrawer,
    Tooltip,
} from '@fluentui/react-components';
import MoreMenuListItems from '../PlacesMoreDropdown/MoreMenuListItems';
import { navigationMenuButtonLabel, settingsDividerText } from './SmallScreenNav.locstring.json';
import {
    bottomSheetContainer,
    buttonActive,
    buttonInactive,
    buttonMargin,
    divider,
    divTitle,
} from './SmallScreenNav.scss';

import type { OnVisibleChangeData } from '@fluentui/react-components';

import type { AccTabItem } from 'accelerator-common/lib/AccTabList';
const HamburgerIcon = <Icon iconName={LineHorizontal3} />;

export interface SmallScreenNavProps {
    links: AccTabItem[];
    navigate: (newSelectedKey: string) => void;
    selectedRoute: string;
    isSmallScreen: boolean;
    isStandaloneApp: boolean;
}
const defaultExport: React.FunctionComponent<SmallScreenNavProps> = observer(
    function SmallScreenNav({
        links,
        navigate,
        selectedRoute,
        isSmallScreen,
        isStandaloneApp,
    }: SmallScreenNavProps) {
        const focusTrapZone = React.useRef<HTMLDivElement | null>(null);

        const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(false);
        const [active, setActive] = useState<boolean>(false);
        const [visible, setVisible] = useState(false);

        // Create a listener if the user hits the escape key to close the popover/bottom sheet
        React.useEffect(() => {
            const handleEscKey = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
                     * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
                     *	> React setState function 'setIsBottomSheetOpen' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
                    setIsBottomSheetOpen(false);
                    /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
                     * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
                     *	> React setState function 'setActive' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
                    setActive(false);
                    /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
                     * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
                     *	> React setState function 'setVisible' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
                    setVisible(false);
                }
            };

            addDocumentEventListener('SmallScreenNav', 'keydown', handleEscKey);
        }, []);

        const handleSelectedKeyChange = React.useCallback(
            (newSelectedKey: string) => {
                navigate(newSelectedKey);
                setIsBottomSheetOpen(false);
                setActive(false);
            },
            [navigate]
        );

        const handleDismiss = React.useCallback(() => {
            setIsBottomSheetOpen(false);
            setActive(false);
            setVisible(false);
        }, []);

        const onSmallScreenButtonClick = React.useCallback(() => {
            setIsBottomSheetOpen(true);
            setActive(true);
            focusTrapZone.current?.focus();
        }, []);

        React.useMemo(() => {
            if (isSmallScreen) {
                setIsBottomSheetOpen(false);
                setActive(false);
            }
        }, [isSmallScreen]);

        const handleVisibleChange = React.useCallback(
            (_ev: any, data: OnVisibleChangeData) => {
                if (isBottomSheetOpen) {
                    setVisible(false);
                } else {
                    setVisible(data.visible);
                }
            },
            [isBottomSheetOpen]
        );

        return (
            <>
                {isSmallScreen && (
                    <>
                        <Tooltip
                            content={loc(navigationMenuButtonLabel)}
                            relationship="label"
                            visible={visible && !isBottomSheetOpen}
                            onVisibleChange={handleVisibleChange}
                        >
                            <Button
                                appearance="transparent"
                                icon={HamburgerIcon}
                                onClick={onSmallScreenButtonClick}
                                className={
                                    isStandaloneApp
                                        ? mergeClasses(
                                              active ? buttonActive : buttonInactive,
                                              buttonMargin
                                          )
                                        : active
                                          ? buttonActive
                                          : buttonInactive
                                }
                                aria-label={loc(navigationMenuButtonLabel)}
                                data-telemetry-id="SmallScreenNavButton"
                            />
                        </Tooltip>
                        <OverlayDrawer
                            position="bottom"
                            open={isBottomSheetOpen}
                            onOpenChange={handleDismiss}
                            className={bottomSheetContainer}
                        >
                            <DrawerBody>
                                <PlacesFluentProvider>
                                    <FocusTrapZone
                                        ref={focusTrapZone}
                                        forceFocusInsideTrap={false}
                                        isClickableOutsideFocusTrap={true}
                                    >
                                        <AccTabList
                                            items={links}
                                            selectedKey={selectedRoute}
                                            onSelectedKeyChange={handleSelectedKeyChange}
                                            isVertical={true}
                                            appearance={'subtle'}
                                        />
                                        <Divider className={divider}></Divider>
                                        <div className={divTitle}>
                                            <Caption1Strong>
                                                {loc(settingsDividerText)}
                                            </Caption1Strong>
                                        </div>
                                        <MoreMenuListItems />
                                    </FocusTrapZone>
                                </PlacesFluentProvider>
                            </DrawerBody>
                        </OverlayDrawer>
                    </>
                )}
            </>
        );
    },
    'SmallScreenNav'
);
export default defaultExport;
